import React, { useCallback, useEffect, useState, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "motion/react";
import { IconArrowRight } from "./ConciergeIcons";
import { useSuggestedQueries, SuggestedQueryItem } from "@brandclub/common-ui";
import {
  TextBox,
  TrendingCardWidget,
  TrendingCardWidgetState,
} from "@brandclub/bc-widgets";

interface TrendingCardData {
  id: string;
  imageUrl: string;
  title: string;
}

// Helper to get font weight value
const getFontWeight = (weight: string): number => {
  const weights: Record<string, number> = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  };
  return weights[weight] || 600;
};

interface ConciergeTrendingCardProps {
  imageUrl: string;
  title: string;
  onClick?: () => void;
  isActive?: boolean;
  styles: TrendingCardWidgetState;
}

const ConciergeTrendingCard: React.FC<ConciergeTrendingCardProps> = ({
  imageUrl,
  title,
  onClick,
  isActive = true,
  styles,
}) => {
  return (
    <motion.div
      className="tw:relative tw:w-[160px] tw:md:w-[224px] tw:h-[320px] tw:md:h-[400px] tw:cursor-pointer tw:shrink-0 tw:overflow-hidden"
      style={{
        borderRadius: styles.cardBorderRadius,
      }}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      animate={{
        opacity: isActive ? 1 : 0.5,
        scale: isActive ? 1 : 0.95,
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <img
        src={imageUrl}
        alt={title}
        className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover"
      />

      <div
        className="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:h-[60%]"
        style={{
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 10%, rgba(255, 255, 255, 0.15) 25%, rgba(255, 255, 255, 0.4) 40%, rgba(255, 255, 255, 0.7) 55%, rgba(255, 255, 255, 0.9) 70%, rgba(255, 255, 255, 1) 85%)",
        }}
      />

      <div className="tw:absolute tw:top-2 tw:md:top-3 tw:left-2 tw:right-2 tw:flex tw:items-center tw:justify-between">
        <div
          className="tw:font-semibold tw:tracking-[0.08px] tw:text-[10px] tw:md:text-[12px]"
          style={{
            backgroundColor: styles.pillBgColor,
            border: `1px solid ${styles.pillBorderColor}`,
            color: styles.pillTextColor,
            padding: `${styles.pillPaddingY}px ${styles.pillPaddingX}px`,
            borderRadius: styles.pillBorderRadius,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          Explore now
        </div>
        <div
          className="tw:w-6 tw:h-6 tw:md:w-8 tw:md:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full"
          style={{
            backgroundColor: styles.arrowBgColor,
            border: `1px solid ${styles.arrowBorderColor}`,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <IconArrowRight
            size={14}
            style={{ color: styles.arrowIconColor }}
          />
        </div>
      </div>

      <div className="tw:absolute tw:bottom-3 tw:md:bottom-4 tw:left-3 tw:right-3">
        <p
          className="tw:tracking-[0.08px] tw:text-[16px] tw:md:text-[20px] tw:leading-[22px] tw:md:leading-[28px]"
          style={{
            color: styles.titleColor,
            fontWeight: getFontWeight(styles.titleFontWeight),
          }}
        >
          {title}
        </p>
      </div>
    </motion.div>
  );
};

interface ConciergeTrendingSearchesProps {
  onSearch: (searchTerm: string) => void;
  brandId?: number;
}

interface CarouselNavButtonProps {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
  styles?: TrendingCardWidgetState;
}

const CarouselNavButton: React.FC<CarouselNavButtonProps> = ({
  direction,
  onClick,
  disabled,
  styles,
}) => {
  return (
    <motion.button
      className="tw:absolute tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:shadow-lg"
      style={{
        width: 40,
        height: 40,
        backgroundColor: styles?.arrowBgColor || "rgba(255, 255, 255, 0.9)",
        border: `1px solid ${styles?.arrowBorderColor || "#ffffff"}`,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        [direction === "prev" ? "left" : "right"]: "8px",
      }}
      onClick={onClick}
      disabled={disabled}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      animate={{ opacity: disabled ? 0.3 : 1 }}
      transition={{ duration: 0.2 }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={styles?.arrowIconColor || "currentColor"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: direction === "prev" ? "rotate(180deg)" : "none" }}
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </motion.button>
  );
};

export const ConciergeTrendingSearches: React.FC<
  ConciergeTrendingSearchesProps
> = ({ onSearch, brandId }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch suggested queries with images
  const { data } = useSuggestedQueries(brandId);

  // Convert queryItems to TrendingCardData format
  const trendingData: TrendingCardData[] = useMemo(() => {
    const queryItems = data?.suggestedQuestions || [];
    return queryItems.map((item: SuggestedQueryItem, index: number) => ({
      id: `query-${index}`,
      imageUrl: item.imageUrl,
      title: item.query,
    }));
  }, [data]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Don't render if no data
  if (trendingData.length === 0) {
    return null;
  }

  return (
    <div className="tw:w-full tw:mt-8 tw:max-w-[1200px] tw:mx-auto">
      <motion.div
        className="tw:text-center tw:mb-4 tw:md:mb-6 tw:px-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <TextBox
          widgetId="concierge-trending-title"
          displayName="Trending Searches Title"
          initialState={{
            content: "A few suggestions to get you started",
            fontSize: 16,
            fontWeight: "semibold",
            color: "#031425",
            textAlign: "center",
            lineHeight: 2,
            letterSpacing: 0.08,
            textTransform: "none",
          }}
        />
      </motion.div>

      <TrendingCardWidget
        widgetId="concierge-trending-cards"
        displayName="Trending Cards"
      >
        {(state) => (
          <div className="tw:relative tw:px-4 tw:md:px-12">
            {/* Navigation buttons - hidden on mobile */}
            <div className="tw:hidden tw:md:block">
              <CarouselNavButton
                direction="prev"
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                styles={state}
              />
              <CarouselNavButton
                direction="next"
                onClick={scrollNext}
                disabled={!canScrollNext}
                styles={state}
              />
            </div>

            {/* Carousel viewport */}
            <div
              ref={emblaRef}
              className="tw:overflow-x-clip tw:overflow-y-visible tw:py-2"
            >
              <motion.div
                className="tw:flex"
                style={{ gap: state.carouselGap }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, staggerChildren: 0.1 }}
              >
                {trendingData.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="tw:shrink-0"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <ConciergeTrendingCard
                      imageUrl={item.imageUrl}
                      title={item.title}
                      onClick={() => onSearch(item.title)}
                      styles={state}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Progress dots - mobile only */}
            <div className="tw:flex tw:justify-center tw:gap-2 tw:mt-4 tw:md:hidden">
              {trendingData.map((_, index) => (
                <motion.div
                  key={index}
                  className={`tw:w-2 tw:h-2 tw:rounded-full ${
                    index === selectedIndex ? "tw:bg-primary" : "tw:bg-black/20"
                  }`}
                  animate={{
                    scale: index === selectedIndex ? 1.2 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                />
              ))}
            </div>
          </div>
        )}
      </TrendingCardWidget>
    </div>
  );
};
